import styled from 'styled-components';
import { rgba } from 'polished';
import { useNetwork } from 'wagmi';

import MetamaskIcon from '#/assets/MetamaskIcon';
import KeyIcon from '#/assets/KeyIcon';
import InProgressIcon from '#/assets/InProgressIcon';

import * as Alert from '#/components/common/alert';
import ButtonLink from '#/components/shared/ButtonLink';

import { cFFFFFF } from '#/colors';

import EthereumWallet from '#/features/ethereum-wallet/wallet';
import {
  useWalletActions,
  useWalletView,
} from '#/features/wallet/wallet-context';
import { logException } from '#/features/logging/logging';
import { useWalletConnectConfig } from '#/features/wallet/wallet-connect-context';

import Step from './Step';
import RememberMe from './RememberMe';
import NoAccessAllowedModal from './NoAccessAllowedModal';
import PrimaryButton from './PrimaryButton';
import { Modal, ModalCloseButton, ModalHeader, ModalTitle } from './Modal';

interface Props {
  readonly isOpen: boolean;
  readonly closeModal: () => void;
}

/**
 * WIP: after we adapt jest tests to run with `useNetwork()`
 *      and other wagmi / viem / WalletConnect hooks
 *      this hook should be moved to `src/features/wallet/wallet-context.tsx`
 *      and derived value exposed as `useWalletView.incorrectNetworkError`
 */
function useIncorrectNetworkError(): string | null {
  const CHAIN_ID_TO_NAME_MAP: Record<number, string> = {
    1: 'Ethereum Mainnet',
    5: 'Goerli Test',
  };
  const walletConfig = useWalletConnectConfig();
  const network = useNetwork();
  const requiredChain = walletConfig.chain;
  const actualChain = network.chain;

  if (actualChain?.id == null) return null;

  const isCorrectNetwork = actualChain.id === requiredChain.id;
  if (isCorrectNetwork) return null;

  const actualChainName =
    CHAIN_ID_TO_NAME_MAP[actualChain.id] ?? actualChain.name;
  const requiredChainName =
    CHAIN_ID_TO_NAME_MAP[requiredChain.id] ?? requiredChain.name;

  return `Incorrect network selected '${actualChainName}'. Please change the network to '${requiredChainName}'(id=${requiredChain.id}) in your wallet and retry.`;
}

export default function WalletModalOnboard(props: Props) {
  const { isOpen, closeModal } = props;

  const walletView = useWalletView();
  const walletActions = useWalletActions();
  const incorrectNetworkError = useIncorrectNetworkError();

  const handleSendRequests = () => {
    (async () => {
      const initializeStarknetWalletResult =
        await walletActions.initializeStarknetWallet();

      if (!initializeStarknetWalletResult.ok) {
        if (initializeStarknetWalletResult.error != null) {
          const error = new Error(
            'Failed to initialize StarkNet wallet during onboarding',
            { cause: initializeStarknetWalletResult.error },
          );
          logException(error);
        }
        return;
      }

      const onboardingResult = await walletActions.performOnboarding();
      if (!onboardingResult.ok) {
        if (onboardingResult.error != null) {
          const error = new Error(
            'Failed to onboard StarkNet wallet during onboarding',
            { cause: onboardingResult.error },
          );
          logException(error);
        }
        return;
      }

      const authResult = await walletActions.performAuth();
      if (!authResult.ok) {
        if (authResult.error != null) {
          const error = new Error(
            'Failed to authenticate StarkNet wallet during onboarding',
            { cause: authResult.error },
          );
          logException(error);
        }
        return;
      }

      setTimeout(() => closeModal(), 1000);
    })().catch((err) => {
      const message = 'Unexpected Error in handleSendRequests';
      logException(new Error(message, { cause: err }));
    });
  };

  const handleDisconnectEthereumWallet = () => {
    EthereumWallet.disconnect()
      .catch((err) => {
        const message = 'Unexpected Error in handleDisconnectEthereumWallet';
        logException(new Error(message, { cause: err }));
      })
      .then(() => {
        closeModal();
        window.location.reload();
      })
      .catch((err) => {
        const message =
          'Unexpected Error when closing modal and reloading page in handleDisconnectEthereumWallet';
        logException(new Error(message, { cause: err }));
      });
  };

  if (walletView.error.includes('UNAUTHORIZED_ETHEREUM_ADDRESS')) {
    return <NoAccessAllowedModal close={closeModal} isOpen={isOpen} />;
  }

  const isContinueDisabled =
    walletView.status !== 'idle' || incorrectNetworkError != null;

  return (
    <Modal isOpen={isOpen} onDismiss={closeModal}>
      <ModalHeader>
        <ModalTitle>
          <MetamaskIcon size={22} /> Link Wallet
        </ModalTitle>
        {walletView.status === 'idle' && (
          <ModalCloseButton onClick={closeModal} />
        )}
      </ModalHeader>

      <Callout>
        You will receive a signature request. Signing is free and will not send
        a transaction.
      </Callout>

      <Step
        icon={<KeyIcon size={24} />}
        heading={PRIVATE_KEY_GENERATION_STEP[walletView.status]}
        description="Enable secure access to our API"
        isActive
        isProgress={walletView.status === 'progress'}
        isComplete={walletView.status === 'complete'}
      />

      <RememberMe />

      <PrimaryButton disabled={isContinueDisabled} onClick={handleSendRequests}>
        {walletView.status === 'idle' ? (
          'Continue'
        ) : (
          <InProgressIcon size={22} />
        )}
      </PrimaryButton>

      <DisconnectWalletButtonLink onClick={handleDisconnectEthereumWallet} />

      {incorrectNetworkError != null && (
        <Alert.Error>{incorrectNetworkError}</Alert.Error>
      )}
      {walletView.error.length > 0 && (
        <Alert.Error>
          {walletView.error}
          <br />
          <br />
          Ethereum Wallet Connected: <br />
          {walletView.ethereumAddress}
          <br />
          <br />
          <DisconnectWalletButton onClick={handleDisconnectEthereumWallet} />
        </Alert.Error>
      )}
    </Modal>
  );
}

const DisconnectWalletButton = styled.button.attrs({
  children: 'Disconnect Wallet',
})`
  border-radius: 7px;
  border: none;
  color: ${cFFFFFF};
  font-size: 13px;
  font-weight: 700;
  display: grid;
  place-items: center;
  width: 100%;
  height: 28px;
  background-color: ${rgba(cFFFFFF, 0.2)};

  &:hover {
    background-color: ${rgba(cFFFFFF, 0.25)};
  }
`;

const DisconnectWalletButtonLink = styled(ButtonLink).attrs({
  children: 'Disconnect',
})`
  font-size: 13px;
  padding: 3px 0;
`;

const PRIVATE_KEY_GENERATION_STEP = {
  idle: 'Generate Private Key',
  progress: 'Generating Private Key',
  complete: 'Private Key Generated',
};

const Callout = styled.div`
  color: ${rgba(cFFFFFF, 0.5)};
  font-size: 11px;
  font-weight: 500;
  line-height: 18px;
`;
